import theme_icons from '../../icons.js';
import { MediaUploaderGeneric } from '../../components/image-picker-generic.js';
import { AdderGeneric } from '../../components/adder-generic.js';
import { IconListRenderer } from '../../components/iconlist-renderer.js';

const { __ } = wp.i18n;
const { registerBlockType } = wp.blocks;
const { useEffect, useState } = wp.element;
const { RichText, InspectorControls, ColorPalette, InnerBlocks } = wp.blockEditor;
const { Button, PanelBody, TextControl, SelectControl, ToggleControl } = wp.components;

const iconlist = {
    title: 'Icon List',
    icon: theme_icons.greenheart,
    category: 'greenheart',
    attributes: {
        variant: {
            type: 'string',
            default: ''
        },
        hasTitles: {
            type: 'boolean',
            default: false
        },
        hasText: {
            type: 'boolean',
            default: true
        },
        hasLinks: {
            type: 'boolean',
            default: false
        },
        hlevel: {
            type: 'number',
            default: 4
        },
        linkStyle: {
            type: 'string',
            default: ''
        },
        items: {
            type: 'array',
            default: []
        },
        isAdding: {
            type: 'boolean',
            default: false
        },
        isEditing: {
            type: 'number',
            default: -1
        },
        currentItem: {
            type: 'object',
            default: {
                icon: {},
                title: '',
                text: '',
                link: ''
            }
        },
        currentId: {
            type: 'number',
            default: 0
        },
        itemColor: {
            type: 'string',
            default: ''
        },
        iconSize: {
            type: 'string',
            default: 'medium'
        },
    },

    edit({ attributes, setAttributes }) {
        const {
            hasTitles, hasLinks, hasText, hlevel, linkStyle, variant, items,
            isAdding, isEditing, currentItem, currentId, itemColor, iconSize
        } = attributes;

        useEffect(() => {
            setAttributes({ currentId: getNextId() });
        }, []);
        useEffect(() => {
            updateItemsWithSVGContent(items);
        }, []);

        const getNextId = () => {
            let i = 1;
            while (items.filter(item => item.id === i).length > 0) { i++; }
            return i;
        };

        const fetchSVG = async (url) => {
            try {
                const response = await fetch(url);
                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'image/svg+xml');
                const svg = doc.querySelector('svg');
                if (svg) {
                    svg.style.fill = 'currentColor';
                    return svg.outerHTML;
                }
                return null;
            } catch (error) {
                console.error('Error fetching SVG:', error);
                return null;
            }
        };

        const updateItemsWithSVGContent = async (items) => {
            const updatedItems = await Promise.all(items.map(async (item) => {
                if (item.icon.mediaUrl && item.icon.mediaUrl.endsWith('.svg')) {
                    const svgContent = await fetchSVGContent(item.icon.mediaUrl);
                    return { ...item, svgContent };
                }
                return { ...item, svgContent: null }; // Clear if not an SVG
            }));
            setAttributes({ items: updatedItems });
        };

        const fetchSVGContent = async (url) => {
            try {
                const response = await fetch(url);
                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'image/svg+xml');
                const svg = doc.querySelector('svg');
                if (svg) {
                    svg.setAttribute('fill', 'currentColor');
                    return svg.outerHTML;
                }
                return null;
            } catch (error) {
                console.error('Error fetching SVG:', error);
                return null;
            }
        };

        const updateCurrentItem = (field, value) => {
            setAttributes({
                currentItem: { ...currentItem, [field]: value }
            });
        };

        const resetForm = () => {
            setAttributes({
                isAdding: false,
                isEditing: -1,
                currentItem: { icon: {}, title: '', text: '', link: '' }
            });
        };

        const addItem = () => {
            setAttributes({
                currentId: getNextId(),
                isAdding: true,
                isEditing: -1,
            });
        };

        const editItem = (id) => {
            const item = items.find(item => item.id === id);
            setAttributes({
                currentItem: { ...item },
                currentId: id,
                isEditing: id,
            });
        };

        const saveItem = async () => {
            const newItem = {
                ...currentItem,
                id: currentId
            };

            if (newItem.icon.mediaUrl && newItem.icon.mediaUrl.endsWith('.svg')) {
                const svgContent = await fetchSVGContent(newItem.icon.mediaUrl);
                newItem.svgContent = svgContent;
            }

            const itemIndex = items.findIndex(item => item.id === currentId);
            const newItems = itemIndex !== -1
                ? items.map(item => item.id === currentId ? newItem : item)
                : [...items, newItem];

            setAttributes({ items: newItems });
            resetForm();
        };

        const moveItem = (index, direction) => {
            if ((direction === 'up' && index === 0) ||
                (direction === 'down' && index === items.length - 1)) return;

            const newItems = [...items];
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
            setAttributes({ items: newItems });
        };

        const deleteItem = (id) => {
            setAttributes({
                items: items.filter(item => item.id !== id)
            });
        };

        const sizeOptions = {
            tiny: '16px',
            small: '24px',
            medium: '48px',
            large: '96px',
            'x-large': '150px'
        };

        const IconWrapper = ({ item }) => {
            if (item.hasOwnProperty('svgContent') && item.svgContent) {
                return <div dangerouslySetInnerHTML={{ __html: item.svgContent }} />;
            }
            if (item.icon.mediaUrl) {
                return <img src={item.icon.mediaUrl} alt="" />;
            }
            console.log("Failed to render icon", item);
            return null;
        };

        return ([
            <InspectorControls>
                <PanelBody title="Icon List Settings">
                    <p>What Kind of List?</p>
                    <SelectControl
                        label="List Variant"
                        value={variant}
                        options={[
                            { label: 'Default', value: '' },
                            { label: 'Grid', value: 'grid' },
                            { label: 'Vertical', value: 'vertical' },
                            { label: 'Horizontal', value: 'horizontal' },
                            { label: 'Accordion', value: 'accordion' }
                        ]}
                        onChange={value => setAttributes({ variant: value })}
                    />
                    <p>Icon Size</p>
                    <SelectControl
                        label="Icon Size"
                        value={iconSize}
                        options={Object.keys(sizeOptions).map(key => ({
                            label: key.charAt(0).toUpperCase() + key.slice(1),
                            value: key
                        }))}
                        onChange={value => setAttributes({ iconSize: value })}
                    />
                    <p>Item Color</p>
                    <ColorPalette
                        label="Item Color"
                        color={itemColor}
                        value={itemColor}
                        onChange={value => setAttributes({ itemColor: value })}
                    />
                    <ToggleControl
                        label="Include Titles"
                        checked={hasTitles}
                        onChange={value => setAttributes({ hasTitles: value })}
                        help="NOTE: If set to false, titles will still be used for alt text on icons."
                    />
                    {hasTitles &&
                        <SelectControl
                            label="heading level"
                            value={hlevel}
                            options={[
                                { label: 'H1', value: 1 },
                                { label: 'H2', value: 2 },
                                { label: 'H3', value: 3 },
                                { label: 'H4 (default)', value: 4 },
                                { label: 'H5', value: 5 },
                                { label: 'H6', value: 6 },
                            ]}
                            onChange={value => setAttributes({ variant: value })}
                        />
                    }
                    <ToggleControl
                        label="Include Links"
                        checked={hasLinks}
                        onChange={value => setAttributes({ hasLinks: value })}
                    />
                    <ToggleControl
                        label="Include Text"
                        checked={hasText}
                        onChange={value => setAttributes({ hasText: value })}
                    />
                    {hasLinks &&
                        <SelectControl
                            label="Link Style"
                            value={linkStyle}
                            options={[
                                { label: 'Link Everything', value: 'all' },
                                { label: 'Link Icon Only', value: 'icon' },
                                { label: 'Link Title Only', value: 'title' },
                                { label: 'Link Title & Text', value: 'content' }
                            ]}
                            onChange={value => setAttributes({ linkStyle: value })}
                        />
                    }
                </PanelBody>
            </InspectorControls>,
            <div className="rbt-iconlist">

                {(isAdding || isEditing > -1) ? (
                    <div className="rbt-iconlist-form">
                        <MediaUploaderGeneric
                            photo={currentItem.icon}
                            isContain={true}
                            updateImage={value => updateCurrentItem('icon', value)}
                        />

                        <RichText
                            tagName="h4"
                            value={currentItem.title}
                            onChange={value => updateCurrentItem('title', value)}
                            placeholder={hasTitles ? "Item Title" : "Item Alt Text"}
                        />

                        <RichText
                            tagName="p"
                            value={currentItem.text}
                            onChange={value => updateCurrentItem('text', value)}
                            placeholder="Item Text"
                        />
                        {hasLinks && (
                            <TextControl
                                label="Link URL"
                                value={currentItem.link}
                                onChange={value => updateCurrentItem('link', value)}
                            />
                        )}
                        <div className="controls">
                            {currentItem.icon.mediaUrl && currentItem.title && (
                                <Button className="button primary" onClick={saveItem}>
                                    Save Item
                                </Button>
                            )}
                            <Button className="button secondary" onClick={resetForm}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    /* Alter Structure and Allow Innerblocks for Accordions */
                    variant === 'accordion' ? (
                        <div className="accordion-list">
                            <ul className="iconlist accordion">
                                {items.map((item, index) => (
                                    <li key={item.id} className="list-item accordion">
                                        <div className="wrap">
                                            <div
                                                className="iconwrap"
                                                onClick={(event) => iconListAccordionClick(event)}
                                            >
                                                <IconWrapper item={item} />
                                            </div>
                                            <div className="textwrap">
                                                {hasTitles && (
                                                    <RichText
                                                        tagName="h4"
                                                        value={item.title}
                                                        onChange={(value) => updateCurrentItem('title', value)}
                                                        placeholder="Accordion Title"
                                                        className="list-title"
                                                    />
                                                )}
                                                <div className="body accordion-panel">
                                                    <InnerBlocks />
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="rbt-iconlist-items">
                            <ul className={`iconlist ${variant}`}>
                                {items.map((item, index) => (
                                    <li key={item.id}
                                        style={{
                                            color: itemColor,
                                            '--icon-size': sizeOptions[iconSize]
                                        }}
                                    >
                                        <div className="wrap">
                                            <div className="iconwrap">
                                                <IconWrapper item={item} />
                                            </div>
                                            <div className="textwrap">
                                                {hasTitles && (
                                                    <h4
                                                        className="list-title"
                                                        dangerouslySetInnerHTML={{ __html: item.title }}
                                                    />
                                                )}
                                                <p
                                                    className="list-text"
                                                    dangerouslySetInnerHTML={{ __html: item.text }}
                                                />
                                            </div>
                                            <div className="controls">
                                                <Button onClick={() => deleteItem(item.id)}>
                                                    Delete
                                                </Button>
                                                <Button onClick={() => editItem(item.id)}>
                                                    Edit
                                                </Button>
                                                <Button
                                                    onClick={() => moveItem(index, 'up')}
                                                    disabled={index === 0}
                                                >
                                                    ⬆️
                                                </Button>
                                                <Button
                                                    onClick={() => moveItem(index, 'down')}
                                                    disabled={index === items.length - 1}
                                                >
                                                    ⬇️
                                                </Button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <AdderGeneric onAdd={addItem} />
                        </div>
                    )
                )}
            </div>
        ]);
    },

    save({ attributes }) {
        const {
            hasTitles, hasLinks, hasText, hlevel, linkStyle, variant, items, itemColor, iconSize
        } = attributes;
        const sizeOptions = {
            tiny: '16px',
            small: '24px',
            medium: '48px',
            large: '96px',
            'x-large': '150px'
        };

        return (
            <div className="block">
                <IconListRenderer
                    items={items}
                    variant={variant}
                    linkStyle={linkStyle}
                    hasTitles={hasTitles}
                    hasText={hasText}
                    hasLinks={hasLinks}
                    hlevel={hlevel}
                    itemColor={itemColor}
                    iconSize={sizeOptions[iconSize]}
                    children={variant === 'accordion' ? <InnerBlocks.Content /> : null}
                />
            </div>
        );
    }
};

registerBlockType('rbt/iconlist', iconlist);
export default iconlist;